/**
 * The installable portal's branding, applied at runtime (doorbell live calls
 * spec, "Installable portal"). The portal is the same static build in the
 * Docker image, on the on-site sidecar and on Garage static hosting, so nothing
 * is written per deployment: `index.html` links the default
 * `/manifest.webmanifest` and nodus icons, which install anywhere. Once the
 * portal knows its site (`GET /tenant-portal/branding/`), this points the
 * manifest link at the API's branded manifest — fetched with CORS, its
 * `start_url`/`scope` built from this page's origin so they stay same-origin —
 * and sets the Home Screen icon and title, which iOS Safari reads from the
 * live page at "Add to Home Screen" time.
 */

export interface PwaBranding {
  /** `getConfig().API_BASE_URL`, e.g. `https://api.nodus.se/api`. */
  apiBaseUrl: string;
  siteId: number | null;
  siteName: string | null;
  /** `window.location.origin`. */
  origin: string;
}

export function brandedManifestUrl(b: PwaBranding): string {
  const query = new URLSearchParams();
  if (b.siteId) query.set("site_id", String(b.siteId));
  query.set("origin", b.origin);
  return `${b.apiBaseUrl}/tenant-portal/branding/manifest.webmanifest?${query.toString()}`;
}

export function brandedIconUrl(b: PwaBranding): string {
  return `${b.apiBaseUrl}/tenant-portal/branding/icon-192.png${b.siteId ? `?site_id=${b.siteId}` : ""}`;
}

function setAttribute(doc: Document, selector: string, name: string, value: string): void {
  const el = doc.querySelector(selector);
  if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
}

/** Brand the manifest, Home Screen icon and title for this site. False (and
 *  nothing changed) while the site is unknown — the static defaults stay. */
export function applyPwaBranding(doc: Document, b: PwaBranding): boolean {
  if (!b.siteId) return false;
  setAttribute(doc, 'link[rel="manifest"]', "href", brandedManifestUrl(b));
  setAttribute(doc, 'link[rel="apple-touch-icon"]', "href", brandedIconUrl(b));
  if (b.siteName) setAttribute(doc, 'meta[name="apple-mobile-web-app-title"]', "content", b.siteName);
  return true;
}
